import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiCamera,
  FiChevronLeft,
  FiChevronRight,
  FiImage,
  FiPauseCircle,
  FiPlayCircle,
} from "react-icons/fi";

import Reveal from "./ui/Reveal";

import {
  getHomeCarouselAlbum,
  toAbsolutePublicUrl,
} from "../services/mediaAlbumServices";

const DEFAULT_INTERVAL_MS = 5500;

function getSlideAlt(item, albumTitle) {
  return (
    item?.alt_text_override ||
    item?.media?.alt_text ||
    item?.caption ||
    item?.media?.title ||
    albumTitle ||
    "Photo de la commune"
  );
}

function getSlideTitle(item) {
  return (
    item?.caption ||
    item?.media?.title ||
    item?.media?.original_name ||
    item?.media?.file_name ||
    ""
  );
}

export default function HomeCommuneCarousel({
  intervalMs = DEFAULT_INTERVAL_MS,
}) {
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [error, setError] = useState("");

  const slides = useMemo(() => {
    const items = Array.isArray(album?.items) ? album.items : [];

    return items
      .filter((item) => item?.is_visible !== false)
      .filter((item) => item?.media?.url)
      .map((item) => ({
        ...item,
        imageUrl: toAbsolutePublicUrl(item.media.url),
        title: getSlideTitle(item),
        alt: getSlideAlt(item, album?.title),
      }));
  }, [album]);

  const hasSlides = slides.length > 0;
  const hasMultipleSlides = slides.length > 1;
  const currentSlide = hasSlides ? slides[activeIndex] : null;

  const fetchCarousel = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const data = await getHomeCarouselAlbum();

      setAlbum(data);
      setActiveIndex(0);
    } catch (fetchError) {
      console.error("Erreur chargement carrousel commune :", fetchError);
      setError("Impossible de charger les photos de la commune.");
      setAlbum(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCarousel();
  }, [fetchCarousel]);

  const goToSlide = useCallback(
    (index) => {
      if (!hasSlides) return;

      const nextIndex = Math.min(Math.max(index, 0), slides.length - 1);
      setActiveIndex(nextIndex);
    },
    [hasSlides, slides.length],
  );

  const goToPrevious = useCallback(() => {
    if (!hasSlides) return;

    setActiveIndex((previous) => {
      if (previous <= 0) return slides.length - 1;
      return previous - 1;
    });
  }, [hasSlides, slides.length]);

  const goToNext = useCallback(() => {
    if (!hasSlides) return;

    setActiveIndex((previous) => {
      if (previous >= slides.length - 1) return 0;
      return previous + 1;
    });
  }, [hasSlides, slides.length]);

  useEffect(() => {
    if (!isPlaying || !hasMultipleSlides) return undefined;

    const timer = window.setInterval(() => {
      setActiveIndex((previous) => {
        if (previous >= slides.length - 1) return 0;
        return previous + 1;
      });
    }, intervalMs);

    return () => {
      window.clearInterval(timer);
    };
  }, [hasMultipleSlides, intervalMs, isPlaying, slides.length]);

  useEffect(() => {
    if (!hasSlides) return;

    if (activeIndex > slides.length - 1) {
      setActiveIndex(0);
    }
  }, [activeIndex, hasSlides, slides.length]);

  if (!loading && (!hasSlides || error)) {
    return null;
  }

  return (
    <section
      id="photos-commune"
      className="relative overflow-hidden bg-white px-4 py-14 dark:bg-slate-950 sm:px-6 lg:px-8 lg:py-20"
      aria-labelledby="photos-commune-title"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-sky-300/15 blur-3xl dark:bg-sky-500/10" />
        <div className="absolute -right-24 bottom-10 h-80 w-80 rounded-full bg-emerald-300/10 blur-3xl dark:bg-emerald-500/10" />
      </div>

      <div className="relative mx-auto max-w-[1380px]">
        <Reveal>
          <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-bold text-slate-700 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#1277B9] text-white">
                  <FiCamera className="h-4 w-4" />
                </span>
                Galerie photos
              </div>

              <h2
                id="photos-commune-title"
                className="mt-5 max-w-3xl text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-4xl lg:text-5xl"
              >
                {album?.title || "Découvrez la commune en images"}
              </h2>

              {album?.description ? (
                <p className="mt-4 max-w-3xl text-base leading-8 text-slate-600 dark:text-slate-300">
                  {album.description}
                </p>
              ) : (
                <p className="mt-4 max-w-3xl text-base leading-8 text-slate-600 dark:text-slate-300">
                  Retrouvez une sélection de photos de la commune, de ses lieux
                  de vie, de son patrimoine et de ses événements.
                </p>
              )}
            </div>

            {hasMultipleSlides ? (
              <button
                type="button"
                onClick={() => setIsPlaying((previous) => !previous)}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-[#1277B9]/30 hover:text-[#1277B9] dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-sky-500/30 dark:hover:text-sky-300"
              >
                {isPlaying ? (
                  <>
                    <FiPauseCircle className="h-5 w-5" />
                    Arrêter
                  </>
                ) : (
                  <>
                    <FiPlayCircle className="h-5 w-5" />
                    Reprendre
                  </>
                )}
              </button>
            ) : null}
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="relative overflow-hidden rounded-[2.4rem] border border-slate-200 bg-white shadow-2xl shadow-slate-950/10 dark:border-slate-800 dark:bg-slate-900">
            <div className="absolute inset-x-0 top-0 z-20 h-2 bg-gradient-to-r from-[#1277B9] via-sky-400 to-emerald-300" />

            {loading ? (
              <div className="flex min-h-[520px] items-center justify-center bg-slate-100 dark:bg-slate-900">
                <div className="text-center">
                  <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-slate-300 border-t-[#1277B9] dark:border-slate-700 dark:border-t-sky-400" />
                  <p className="mt-4 text-sm font-bold text-slate-500 dark:text-slate-400">
                    Chargement des photos...
                  </p>
                </div>
              </div>
            ) : currentSlide ? (
              <>
                <div className="relative min-h-[360px] overflow-hidden bg-slate-100 sm:min-h-[500px] lg:min-h-[620px] dark:bg-slate-950">
                  {slides.map((slide, index) => {
                    const active = index === activeIndex;

                    return (
                      <div
                        key={slide.id || slide.media_id}
                        className={[
                          "absolute inset-0 transition duration-700 ease-out",
                          active
                            ? "z-10 opacity-100"
                            : "z-0 scale-105 opacity-0",
                        ].join(" ")}
                        aria-hidden={!active}
                      >
                        <img
                          src={slide.imageUrl}
                          alt={slide.alt}
                          loading={index === 0 ? "eager" : "lazy"}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    );
                  })}

                  {hasMultipleSlides ? (
                    <>
                      <button
                        type="button"
                        onClick={goToPrevious}
                        className="absolute left-4 top-1/2 z-30 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/70 bg-white/75 text-slate-800 shadow-lg backdrop-blur transition hover:scale-105 hover:bg-white hover:text-[#1277B9] dark:border-white/15 dark:bg-slate-950/65 dark:text-white dark:hover:bg-slate-950 sm:left-6 sm:h-14 sm:w-14"
                        aria-label="Photo précédente"
                      >
                        <FiChevronLeft className="h-6 w-6" />
                      </button>

                      <button
                        type="button"
                        onClick={goToNext}
                        className="absolute right-4 top-1/2 z-30 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/70 bg-white/75 text-slate-800 shadow-lg backdrop-blur transition hover:scale-105 hover:bg-white hover:text-[#1277B9] dark:border-white/15 dark:bg-slate-950/65 dark:text-white dark:hover:bg-slate-950 sm:right-6 sm:h-14 sm:w-14"
                        aria-label="Photo suivante"
                      >
                        <FiChevronRight className="h-6 w-6" />
                      </button>
                    </>
                  ) : null}
                </div>

                <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900 sm:p-5 lg:p-6">
                  {hasMultipleSlides ? (
                    <div className="mb-5 h-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-[#1277B9] to-emerald-300 transition-all duration-300"
                        style={{
                          width: `${((activeIndex + 1) / slides.length) * 100}%`,
                        }}
                      />
                    </div>
                  ) : null}

                  <div className="grid gap-5 lg:grid-cols-[1fr_auto] lg:items-start">
                    <div>
                      <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
                        <FiImage className="h-4 w-4" />
                        Photo {activeIndex + 1} / {slides.length}
                      </div>

                      <h3 className="mt-3 text-2xl font-bold leading-tight text-slate-950 dark:text-white sm:text-3xl">
                        {currentSlide.title ||
                          album?.title ||
                          "Photo de la commune"}
                      </h3>

                      
                    </div>

                    {hasMultipleSlides ? (
                      <div className="flex items-center gap-2 lg:justify-end">
                        <button
                          type="button"
                          onClick={goToPrevious}
                          className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-700 transition hover:border-[#1277B9]/30 hover:bg-white hover:text-[#1277B9] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                          aria-label="Photo précédente"
                        >
                          <FiChevronLeft className="h-5 w-5" />
                        </button>

                        <button
                          type="button"
                          onClick={goToNext}
                          className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-700 transition hover:border-[#1277B9]/30 hover:bg-white hover:text-[#1277B9] dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                          aria-label="Photo suivante"
                        >
                          <FiChevronRight className="h-5 w-5" />
                        </button>
                      </div>
                    ) : null}
                  </div>

                  {hasMultipleSlides ? (
                    <div className="mt-6 flex gap-3 overflow-x-auto pb-1">
                      {slides.map((slide, index) => {
                        const active = index === activeIndex;

                        return (
                          <button
                            key={slide.id || slide.media_id}
                            type="button"
                            onClick={() => goToSlide(index)}
                            className={[
                              "relative h-20 w-28 shrink-0 overflow-hidden rounded-2xl border transition sm:h-24 sm:w-36",
                              active
                                ? "border-sky-400 ring-4 ring-sky-100 dark:border-sky-400 dark:ring-sky-500/10"
                                : "border-slate-200 opacity-70 hover:opacity-100 dark:border-slate-700",
                            ].join(" ")}
                            aria-label={`Afficher la photo ${index + 1}`}
                          >
                            <img
                              src={slide.imageUrl}
                              alt=""
                              loading="lazy"
                              className="h-full w-full object-cover"
                            />

                            <span
                              className={[
                                "absolute bottom-2 left-2 rounded-full px-2 py-0.5 text-xs font-bold shadow-sm",
                                active
                                  ? "bg-[#1277B9] text-white"
                                  : "bg-white/85 text-slate-700 backdrop-blur dark:bg-slate-950/75 dark:text-white",
                              ].join(" ")}
                            >
                              {index + 1}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </>
            ) : (
              <div className="flex min-h-[420px] items-center justify-center bg-slate-100 p-8 text-center dark:bg-slate-900">
                <div>
                  <FiImage className="mx-auto text-5xl text-slate-300 dark:text-slate-600" />

                  <h3 className="mt-4 text-xl font-bold text-slate-950 dark:text-white">
                    Aucun carrousel photo disponible
                  </h3>

                  <p className="mt-2 max-w-md text-sm leading-6 text-slate-500 dark:text-slate-400">
                    Créez un album actif, public et marqué pour l’accueil depuis
                    l’administration.
                  </p>
                </div>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}